import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, Sprout, ClipboardCheck, Heart, MessageCircle, Apple, Users, LineChart, BookOpen } from 'lucide-react';
import { Logo } from '../../Logo';
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
export function Home() {
  return <div className="w-full" data-id="element-2594" data-mp="v9naoobp3ic0">
      {/* SPLIT HERO */}
      <section className="relative min-h-[426px] md:h-[426px] flex items-center px-6 pt-[59px] bg-cover bg-center" style={{
      backgroundImage: `url('https://cdn.magicpatterns.com/uploads/7Nio93yQvpecyCZYaLP2wa/file_00000000403871f89bf6d32bcc72887f.png')`
    }} data-id="element-2595" data-mp="qdoiepn8xg6b">
        {/* Cream gradient overlay for text readability on left */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#F9F9F9]/95 via-[#F9F9F9]/70 to-transparent pointer-events-none" data-id="element-2596" data-mp="1tk443yiigr7w" />

        <div className="relative w-full max-w-7xl mx-auto px-6" data-id="element-2597" data-mp="1fu0m5jqnzjo9">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-2598" data-mp="1jub29p867qgu">
            {/* Left: Text Content */}
            <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="flex flex-col pt-2 md:pt-0" data-id="element-2599" data-mp="27l876i1jtnil">
              <motion.p variants={fadeUpVariants} className="text-[10px] md:text-[11px] font-bold text-tealslate uppercase tracking-[0.25em] mb-3" data-id="element-2600" data-mp="1yifdk84w8zpl">
                Jack Achenbach · MS, RDN, CPT
              </motion.p>

              <motion.h1 variants={fadeUpVariants} className="text-3xl md:text-4xl lg:text-5xl font-serif text-[#1F1F1F] leading-[1.05] mb-1" data-id="element-2601" data-mp="s34zf112fenv">
                Structured nutrition.
              </motion.h1>

              <motion.h1 variants={fadeUpVariants} className="text-3xl md:text-4xl lg:text-5xl font-serif text-tealslate italic leading-[1.05] mb-4" data-id="element-2602" data-mp="2ou0y9dm17a0x">
                Real results.
              </motion.h1>

              <motion.p variants={fadeUpVariants} className="text-sm md:text-base text-[#3B3B3B] max-w-lg mb-5 leading-relaxed" data-id="element-2603" data-mp="2b2m3k9irhke1">
                Evidence-based nutrition coaching for GLP-1 support, body
                composition, and sustainable metabolic health.
              </motion.p>

              <motion.div variants={fadeUpVariants} className="flex flex-col sm:flex-row items-start gap-3" data-id="element-2604" data-mp="1x3w4wpgancnk">
                <Link to="/contact" className="px-6 py-3 bg-tealslate text-white text-xs font-bold uppercase tracking-wider hover:bg-[#4A5A5F] transition-colors" data-id="element-2605" data-mp="3rft1sxxi9aq0">
                  Book a Consultation
                </Link>
              </motion.div>
            </motion.div>

            {/* Right side intentionally empty — background image shows through */}
            <div className="hidden lg:block" data-id="element-2606" data-mp="1r080mvz5scj7" />
          </div>
        </div>
      </section>

      {/* VALUE PROPS STRIP */}
      <section className="bg-white border-t border-[#E5E5E5] py-16 px-6" data-id="element-2607" data-mp="ub0nocclvbqw">
        <div className="max-w-7xl mx-auto" data-id="element-2608" data-mp="z80ljzzax86x">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12" data-id="element-2609" data-mp="z925yv27j71b">
            {[{
            icon: Sprout,
            title: 'Personalized Plans',
            desc: 'Nutrition tailored to your goals.'
          }, {
            icon: ClipboardCheck,
            title: 'Evidence-Based',
            desc: 'Built on peer-reviewed research, not trends.'
          }, {
            icon: Heart,
            title: 'Sustainable Habits',
            desc: 'Strategies that outlast the prescription.'
          }, {
            icon: MessageCircle,
            title: 'Ongoing Support',
            desc: 'Direct access to a coach who answers.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.6,
            delay: i * 0.1
          }} className="flex flex-col items-center text-center" data-id="element-2610" data-mp="315sx1uoft8dh" data-mpi={i}>
                <item.icon className="w-7 h-7 text-tealslate mb-4" data-id="element-2611" data-mp="3evo2iio2pcmg" />
                <h3 className="text-base font-serif text-[#1F1F1F] mb-2" data-id="element-2612" data-mp="2fg2xvi2gw8f">
                  {item.title}
                </h3>
                <p className="text-sm text-[#5A5A5A]" data-id="element-2613" data-mp="7b576c5171b2">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* ABOUT ME BLOCK */}
      <section className="bg-[#F9F9F9] py-24 md:py-32 px-6" data-id="element-2614" data-mp="3tiv5a13en4a9">
        <div className="max-w-7xl mx-auto" data-id="element-2615" data-mp="3fsrlrhvwtooi">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-2616" data-mp="3frqapdew926s">
            {/* Left: Food Photo */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="relative" data-id="element-2617" data-mp="3klkt8dirjb4a">
              <img src="https://cdn.magicpatterns.com/uploads/fBWp7VcHydeQL4QeV4Xzp2/pasted-image.png" alt="Jack Achenbach" className="w-full h-[500px] object-cover object-top shadow-xl" data-id="element-2618" data-mp="17d1pynj68qld" />
            </motion.div>

            {/* Right: Content */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-2619" data-mp="tm6mjqrfz1p6">
              <p className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em] mb-4" data-id="element-2620" data-mp="1hd2audwovero">
                About Me
              </p>
              <h2 className="text-4xl md:text-5xl font-serif text-[#1F1F1F] mb-3" data-id="element-2621" data-mp="5x3v5r709uhh">
                Hi, I'm Jack.
              </h2>
              <p className="text-base text-[#5A5A5A] mb-6" data-id="element-2622" data-mp="vo85tl9pdwg">
                Registered Dietitian Nutritionist · Certified Personal Trainer
              </p>
              <div className="w-12 h-0.5 bg-tealslate mb-6" data-id="element-2623" data-mp="2himj9dd5l6bf" />
              <p className="text-base md:text-lg text-[#3B3B3B] leading-relaxed mb-8" data-id="element-2624" data-mp="2hh1mdh3d9bae">
                I help people lose weight, build muscle, improve their
                nutrition, and create habits that last. My approach combines
                clinical evidence with real-world coaching, whether someone is
                using a GLP-1 medication or building change without one.
                Structure is what turns effort into transformation!
              </p>
              <Link to="/services" className="inline-block px-8 py-4 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-[#4A5A5F] transition-colors" data-id="element-2625" data-mp="359i9i61yntgk">
                More About Me
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HOW I CAN HELP */}
      <section className="bg-[#F1F1F1] py-8 md:py-12 px-6" data-id="element-2626" data-mp="vziw6aeds0uv">
        <div className="max-w-7xl mx-auto" data-id="element-2627" data-mp="r18x3opjcg3z">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="text-center mb-6" data-id="element-2628" data-mp="r069pqrf8vst">
            <h2 className="text-3xl md:text-4xl font-serif text-[#1F1F1F] mb-3" data-id="element-2629" data-mp="8350tuuqybjl">
              How I Can Help
            </h2>
            <div className="w-16 h-0.5 bg-tealslate mx-auto" data-id="element-2630" data-mp="m3pjmr0nw7d3" />
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-2631" data-mp="h204az6ep7j7">
            {[{
            icon: Apple,
            title: '1:1 GLP-1 Coaching',
            desc: 'Personalized nutrition guidance to maximize results and minimize side effects.'
          }, {
            icon: Users,
            title: 'Body Composition',
            desc: 'Protein and training protocols to preserve lean mass during weight loss.'
          }, {
            icon: LineChart,
            title: 'Off-Medication Strategy',
            desc: 'Exit plans and habit systems to maintain results after stepping down.'
          }, {
            icon: BookOpen,
            title: 'Education & Resources',
            desc: 'Guides, supplements, and tools that simplify metabolic health.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.6,
            delay: i * 0.1
          }} className="bg-white p-5 flex flex-col items-center text-center border border-[#E5E5E5] hover:shadow-lg transition-shadow" data-id="element-2632" data-mp="2ez08452bjavl" data-mpi={i}>
                <div className="w-12 h-12 rounded-full bg-tealslate/10 flex items-center justify-center mb-4" data-id="element-2633" data-mp="2i0l774vu6jo4">
                  <item.icon className="w-6 h-6 text-tealslate" data-id="element-2634" data-mp="23byiwdo8ehkr" />
                </div>
                <h3 className="text-xl font-serif text-[#1F1F1F] mb-3" data-id="element-2635" data-mp="284i33thfcayj">
                  {item.title}
                </h3>
                <p className="text-sm text-[#5A5A5A] leading-relaxed" data-id="element-2636" data-mp="1udwju3o25z04">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* TRAINING APP TEASER - image background with overlaid text */}
      <section className="relative px-6 border-t border-darkgray overflow-hidden bg-cover bg-center" style={{
      backgroundImage: "url('https://cdn.magicpatterns.com/uploads/btHwB7Y6BD4NPyCHt86Hf7/app.png')"
    }} data-id="element-2637" data-mp="1pbxi0y7yva5a">
        {/* Dark gradient overlay for text legibility on the left */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/20" data-id="element-2638" data-mp="1fna1lhb54wad" />

        <div className="relative z-10 max-w-7xl mx-auto py-16 md:py-24" data-id="element-2639" data-mp="1ascuifxf8x04">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.8
        }} className="max-w-xl" data-id="element-2640" data-mp="1oogjnutfuf5q">
            <p className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em] mb-2" data-id="element-2641" data-mp="27gsk2c0j76d8">
              Structured Training App
            </p>
            <h2 className="text-4xl md:text-5xl font-serif text-white leading-tight mb-4" data-id="element-2642" data-mp="2ainltkqsobpj">
              Programmed workouts. In your pocket.
            </h2>
            <div className="w-10 h-0.5 bg-tealslate mb-3" data-id="element-2643" data-mp="2ff49yexzuq2m" />
            <p className="text-sm md:text-base text-[#D8D8D0] leading-relaxed mb-3 max-w-xl" data-id="element-2644" data-mp="2kfa4c2vneqoe">
              Coach-built training designed to preserve muscle, build strength,
              and maximize your GLP-1 results. Video demos, progress tracking,
              and direct messaging — all for less than a coffee a week.
            </p>

            <div className="flex items-baseline gap-2 mb-5" data-id="element-2645" data-mp="2y9t03vh36vxq">
              <span className="text-4xl font-serif text-white" data-id="element-2646" data-mp="3384b49j4bchj">$6.99</span>
              <span className="text-xs text-[#9CA3AF] uppercase tracking-widest" data-id="element-2647" data-mp="386fk6i1l9iyp">
                / month
              </span>
            </div>

            <div className="flex flex-col sm:flex-row gap-3" data-id="element-2648" data-mp="18umkuerbw1it">
              <Link to="/training#top" className="px-6 py-3 bg-tealslate text-white text-xs font-bold uppercase tracking-wider hover:bg-lightteal transition-colors text-center inline-flex items-center justify-center gap-2" data-id="element-2649" data-mp="1dr39rb6cqjwy">
                Explore Training <ArrowRight className="w-4 h-4" data-id="element-2650" data-mp="3i9b5hlsr7h4n" />
              </Link>
              <a href="https://www.trainerize.me/profile/structured/?planGUID=0adc7cc9d3274ca183f4a7ca2a0e16a3" target="_blank" rel="noopener noreferrer" className="px-6 py-3 border border-[#D8D8D0] text-[#D8D8D0] text-xs font-bold uppercase tracking-wider hover:bg-white/10 transition-colors text-center" data-id="element-2651" data-mp="3i7gg9vag92ur">
                Sign Up Now
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA BANNER - full-width image with text overlaid */}
      <section className="relative w-full overflow-hidden" data-id="element-2652" data-mp="2pmqdqk8d930k">
        <div className="relative w-full bg-cover bg-center min-h-[320px] md:min-h-[380px]" style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=1800&q=80')"
      }} data-id="element-2653" data-mp="2klj74ohym9vh">
          {/* Left gradient overlay for text legibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#F9F9F9] via-[#F9F9F9]/85 to-transparent" data-id="element-2654" data-mp="3dzbdgddjs61" />

          <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 flex items-center min-h-[320px] md:min-h-[380px]" data-id="element-2655" data-mp="8cakfovuhyn7">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} className="max-w-xl" data-id="element-2656" data-mp="102ylz21fl61e">
              <h2 className="text-4xl md:text-5xl font-serif text-[#1F1F1F] mb-5 leading-tight" data-id="element-2657" data-mp="3bs802dcrzysx">
                Ready to take the next step?
              </h2>
              <p className="text-base md:text-lg text-[#3B3B3B] mb-8 leading-relaxed" data-id="element-2658" data-mp="3475ih0e0d7tr">
                Let's build a nutrition plan that works for your body and your
                life.
              </p>
              <Link to="/contact" className="inline-block px-8 py-4 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-[#4A5A5F] transition-colors" data-id="element-2659" data-mp="3d7kfgx0c4bd3">
                Book a Consultation
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>;
}